import * as React from "react";
import type { PlannedOutcome } from "@/lib/dialer";
import { clearTab, readTab, writeTab } from "@/softphone/tab-storage";

/**
 * What the voicemail suggestion (ADR-106 in `platform`) remembers between a rep's click and the
 * end of the call: the outcome it chose for them, and the calls on which they closed it.
 *
 * Module state, like the consult a setter asked for (`acd-call.tsx`): the suggestion is drawn in
 * the phone's rail and the outcome is recorded when the call's window opens, by the window if it
 * is on screen and by the dock if the phone is closed. The planned outcome is also kept for the
 * tab, so a reload between the hangup and the window still records it. It names the call, so a
 * plan nothing got to use cannot be read as the next call's.
 */

const KEY = "acl.planned-outcome";

function stored(): PlannedOutcome | null {
  try {
    const value: unknown = JSON.parse(readTab(KEY) ?? "null");
    if (typeof value !== "object" || value === null) return null;
    const { callId, slug } = value as Record<string, unknown>;
    return typeof callId === "string" && typeof slug === "string" ? { callId, slug } : null;
  } catch {
    return null;
  }
}

let planned: PlannedOutcome | null = stored();
let dismissed: ReadonlySet<string> = new Set();
const watchers = new Set<() => void>();

function changed(): void {
  for (const notify of watchers) notify();
}

function watch(notify: () => void): () => void {
  watchers.add(notify);
  return () => {
    watchers.delete(notify);
  };
}

/** The outcome to record for this call when it ends, without asking the rep. */
export function planOutcome(callId: string, slug: string): void {
  planned = { callId, slug };
  writeTab(KEY, JSON.stringify(planned));
  changed();
}

/** Drop the plan: it was used, it failed, or the campaign does not offer that outcome. */
export function forgetPlannedOutcome(callId: string): void {
  if (planned?.callId !== callId) return;
  planned = null;
  clearTab(KEY);
  changed();
}

export function usePlannedOutcome(): PlannedOutcome | null {
  return React.useSyncExternalStore(watch, () => planned, () => planned);
}

const claimed = new Set<string>();

/** True once per call, for the first to ask. A plan is read in more than one place (the outcome
 *  window wherever it is drawn, and the dock for a phone that is closed), and one of them records
 *  it. */
export function claimPlannedOutcome(callId: string): boolean {
  if (claimed.has(callId)) return false;
  claimed.add(callId);
  return true;
}

/**
 * The rep closed the suggestion on this call with its X. In the phone's rail the choices go and
 * the icon stays, still, to open them again; in a narrow window, which has no rail, the card
 * goes and stays gone for the call (`softphone/voicemail-suggestion.tsx`).
 */
export function dismissSuggestion(callId: string): void {
  dismissed = new Set([...dismissed, callId]);
  changed();
}

export function useDismissedSuggestions(): ReadonlySet<string> {
  return React.useSyncExternalStore(watch, () => dismissed, () => dismissed);
}
